<template>
  <div
    :class="{
      'to-front-landscape': currentContent.type === 'elements.FEEDBACK',
      'to-front-force': websiteInFront,
      'behind-overlay': hasMediaContent,
      'processing-action': isProcessingAction,
    }"
    class="avatar-wrapper"
  >
    <avatar-scene
      v-show="!isHideAvatar"
      id="avatar"
      :avatar="avatarUrl"
      :config="avatarConfig"
      class="avatar-canvas"
      @progress="(p) => $emit('progress', p)"
      @ready="$emit('ready')"
    />
  </div>
</template>

<script>
import { computed, watch, nextTick } from 'vue';
import { AvatarScene, useAvatarState } from '@ht/lib-plural-vue';
import useConfig from '@/composables/useConfig';
import useContent from '@/composables/useContent';
import { WEBSITE_CONTAINER_SIZES } from '@/js/constants';

export default {
  name: 'AvatarComponent',

  components: {
    AvatarScene,
  },

  emits: ['ready', 'progress'],

  props: {
    isProcessingAction: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const { initialConfig } = useConfig();
    const { findContentAssetUrl, currentContent, hasMediaContent, isHideAvatar } = useContent();
    const { setVisible } = useAvatarState();

    const avatarUrl = computed(() => (initialConfig.value.avatar ? findContentAssetUrl(`${initialConfig.value.avatar}.glb`, { useMain: true }) : ''));
    const avatarConfig = computed(() => ({
      assetsBaseUrl: initialConfig.value.assetsBaseUrl,
      ...initialConfig.value.avatarSettings,
    }));

    const websiteInFront = computed(() => currentContent.value
      .attrs?.containerSize === WEBSITE_CONTAINER_SIZES.FULLSCREEN_IN_FRONT);

    watch(isHideAvatar, (val) => {
      nextTick(() => {
        setVisible(!val);
      });
    }, { immediate: true });

    return {
      currentContent,
      hasMediaContent,
      websiteInFront,
      avatarUrl,
      avatarConfig,
      isHideAvatar,
    };
  },
};
</script>

<style lang="scss" scoped>
.avatar-wrapper {
  width: 100%;
  height: 100% !important;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  z-index: 3;
  outline: none;
  flex-shrink: 0;

  &.opacity {
    opacity: 0.1;
  }

  &.behind-overlay {
    z-index: 2;
  }

  @media screen and (orientation: landscape) and (min-width: $desktop_s_breakpoint) {
    position: relative;
    width: 100%;
    margin-left: -25%;
    margin-bottom: 0;

    &.behind-overlay {
      z-index: 2;
    }
  }

  @media screen and (max-height: $mobile_height_breakpoint) {
    position: relative;
    width: 100%;
    margin-left: -25%;
    margin-bottom: 0;

    &.behind-overlay {
      z-index: 2;
    }
  }

  @media screen and (min-height: $big_vertical_screen_breakpoint) {
    width: 100%;
    height: 100% !important;
    position: absolute;

    &.behind-overlay {
      z-index: 1;
    }
  }

  &.to-front-landscape {
    @media screen and (orientation: landscape) and (min-width: $desktop_s_breakpoint) {
      z-index: 3;
    }

    @media screen and (max-height: $mobile_height_breakpoint) {
      z-index: 3;
    }

    @media screen and (min-height: $big_vertical_screen_breakpoint) {
      z-index: 2;

      &.behind-overlay {
        z-index: 1;
      }
    }
  }

  &.to-front-force {
    pointer-events: none;
    z-index: 4;
  }

  &.processing-action {
    @media screen and (orientation: landscape) and (min-width: $desktop_s_breakpoint) {
      pointer-events: none;
      z-index: 5;
    }

    @media screen and (max-height: $mobile_height_breakpoint) {
      z-index: 5;
    }

    @media screen and (min-height: $big_vertical_screen_breakpoint) {
      z-index: 4;
    }
  }

  .avatar-canvas {
    position: relative;
    width: 100%;
    height: 100%;
    outline: none;
  }
}
</style>
